import {
  OverdueAssignmentsQueryParams,
  Persona,
  Replica,
} from '@/types/assignments';
import { httpService } from '@/utils/httpService';
import type {
  AssignmentResponse,
  AssignmentCreate,
  AssignmentUpdate,
  AssignmentSummary,
  AssignmentStatus,
  AssignmentsQueryParams,
  SearchAssignmentsParams,
  EditorialAngleRequest,
  AvatarBriefingRequest,
  DraftContentRequest,
  AIContentStatus,
  EditorialAngleGenerationParams,
  InterviewParams,
  PublishParams,
} from '@/types/api';

// Basic CRUD operations
async function getAssignments(
  params: AssignmentsQueryParams = {}
): Promise<AssignmentResponse[]> {
  return httpService.get<AssignmentResponse[]>('/assignments/', { params });
}

async function getAssignment(
  assignmentId: string
): Promise<AssignmentResponse> {
  return httpService.get<AssignmentResponse>(`/assignments/${assignmentId}`);
}

async function createAssignment(
  assignment: AssignmentCreate
): Promise<AssignmentResponse> {
  return httpService.post<AssignmentResponse>('/assignments/', assignment);
}

async function updateAssignment(
  assignmentId: string,
  update: AssignmentUpdate
): Promise<AssignmentResponse> {
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}`,
    update
  );
}

async function deleteAssignment(
  assignmentId: string
): Promise<{ message: string }> {
  return httpService.delete<{ message: string }>(
    `/assignments/${assignmentId}`
  );
}

// Specialized queries
/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function getActiveAssignments(
  params: { skip?: number; limit?: number } = {}
): Promise<AssignmentResponse[]> {
  return httpService.get<AssignmentResponse[]>('/assignments/active', {
    params,
  });
}

async function getAssignmentsSummary(
  params: { skip?: number; limit?: number } = {}
): Promise<AssignmentSummary[]> {
  return httpService.get<AssignmentSummary[]>('/assignments/summary', {
    params,
  });
}

async function getOverdueAssignments(
  params: OverdueAssignmentsQueryParams
): Promise<AssignmentResponse[]> {
  const query = new URLSearchParams({
    current_datetime: params.current_datetime,
    ...(params.skip ? { skip: params.skip.toString() } : {}),
    ...(params.limit ? { limit: params.limit.toString() } : {}),
  });

  return httpService.get<AssignmentResponse[]>(
    `/assignments/overdue?${query.toString()}`
  );
}

async function searchAssignments(
  params: SearchAssignmentsParams
): Promise<AssignmentResponse[]> {
  return httpService.get<AssignmentResponse[]>('/assignments/search', {
    params,
  });
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function getAssignmentsByTip(
  tipId: string
): Promise<AssignmentResponse[]> {
  return httpService.get<AssignmentResponse[]>(`/assignments/tip/${tipId}`);
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function getAssignmentsBySource(
  sourceId: string
): Promise<AssignmentResponse[]> {
  return httpService.get<AssignmentResponse[]>(
    `/assignments/source/${sourceId}`
  );
}

// Status management
async function updateAssignmentStatus(
  assignmentId: string,
  status: AssignmentStatus
): Promise<AssignmentResponse> {
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/status`,
    undefined,
    { params: { status } }
  );
}

// Content management
async function setEditorialAngle(
  assignmentId: string,
  editorialAngle: string
): Promise<AssignmentResponse> {
  const data: EditorialAngleRequest = { editorial_angle: editorialAngle };
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/editorial-angle`,
    data
  );
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function setAvatarBriefing(
  assignmentId: string,
  briefing: string
): Promise<AssignmentResponse> {
  const data: AvatarBriefingRequest = { avatar_briefing: briefing };
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/avatar-briefing`,
    data
  );
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function setInterviewScript(
  assignmentId: string,
  script: string
): Promise<AssignmentResponse> {
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/interview-script`,
    undefined,
    { params: { interview_script: script } }
  );
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function setDraftContent(
  assignmentId: string,
  content: string
): Promise<AssignmentResponse> {
  const data: DraftContentRequest = { draft_content: content };
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/draft`,
    data
  );
}

// Interview workflow
/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function startInterview(
  assignmentId: string,
  interviewUrl: string
): Promise<AssignmentResponse> {
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/start-interview`,
    undefined,
    { params: { interview_url: interviewUrl } }
  );
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function completeInterview(
  assignmentId: string,
  params: Partial<InterviewParams> = {}
): Promise<AssignmentResponse> {
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/complete-interview`,
    undefined,
    { params }
  );
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function setTranscript(
  assignmentId: string,
  transcriptUrl: string
): Promise<AssignmentResponse> {
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/transcript`,
    undefined,
    { params: { transcript_url: transcriptUrl } }
  );
}

// Content workflow
/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function submitForReview(
  assignmentId: string
): Promise<AssignmentResponse> {
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/submit-review`
  );
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function approveAssignment(
  assignmentId: string
): Promise<AssignmentResponse> {
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/approve`
  );
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function publishAssignment(
  assignmentId: string,
  params: PublishParams
): Promise<AssignmentResponse> {
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/publish`,
    undefined,
    { params }
  );
}

// Consent and metadata
/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function recordConsent(
  assignmentId: string,
  consentRecordingUrl: string
): Promise<AssignmentResponse> {
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/consent`,
    undefined,
    { params: { consent_recording_url: consentRecordingUrl } }
  );
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function setKeyQuotes(
  assignmentId: string,
  keyQuotes: string
): Promise<AssignmentResponse> {
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/key-quotes`,
    undefined,
    { params: { key_quotes: keyQuotes } }
  );
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function setFactualClaims(
  assignmentId: string,
  factualClaims: string
): Promise<AssignmentResponse> {
  return httpService.patch<AssignmentResponse>(
    `/assignments/${assignmentId}/factual-claims`,
    undefined,
    { params: { factual_claims: factualClaims } }
  );
}

// AI content generation
async function generateEditorialAngle(
  tipId: string,
  params: EditorialAngleGenerationParams = {}
): Promise<any> {
  return httpService.post<any>(
    `/assignments/tip/${tipId}/generate-editorial-angle`,
    undefined,
    { params }
  );
}

async function generateAssignmentEditorialAngle(
  assignmentId: string
): Promise<any> {
  return httpService.post<any>(
    `/assignments/${assignmentId}/generate-editorial-angle`
  );
}

async function generateAvatarBriefing(assignmentId: string): Promise<any> {
  return httpService.post<any>(
    `/assignments/${assignmentId}/generate-briefing`
  );
}

async function generateInterviewScript(assignmentId: string): Promise<any> {
  return httpService.post<any>(`/assignments/${assignmentId}/generate-script`);
}

// Persona creation (updated to accept replica_id)
async function createPersona(
  assignmentId: string,
  replicaId?: string
): Promise<Persona> {
  const data = replicaId ? { replica_id: replicaId } : {};
  return httpService.post<Persona>(
    `/assignments/${assignmentId}/create-persona`,
    data
  );
}

// Fetch replicas
async function getReplicas(): Promise<Replica[]> {
  return httpService.get<Replica[]>(`/replicas/`);
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function regenerateAllAIContent(assignmentId: string): Promise<any> {
  return httpService.post<any>(
    `/assignments/${assignmentId}/regenerate-all-ai-content`
  );
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function getAIContentStatus(
  assignmentId: string
): Promise<AIContentStatus> {
  return httpService.get<AIContentStatus>(
    `/assignments/${assignmentId}/ai-content-status`
  );
}

/** @deprecated Unused in the client; scheduled for removal once backend parity is confirmed. */
async function generateBriefingPrompt(
  assignmentId: string
): Promise<Record<string, string>> {
  return httpService.get<Record<string, string>>(
    `/assignments/${assignmentId}/briefing-prompt`
  );
}

export const assignmentsService = {
  getAssignments,
  getAssignment,
  createAssignment,
  updateAssignment,
  deleteAssignment,
  getActiveAssignments,
  getAssignmentsSummary,
  getOverdueAssignments,
  searchAssignments,
  getAssignmentsByTip,
  getAssignmentsBySource,
  updateAssignmentStatus,
  setEditorialAngle,
  setAvatarBriefing,
  setInterviewScript,
  setDraftContent,
  startInterview,
  completeInterview,
  setTranscript,
  submitForReview,
  approveAssignment,
  publishAssignment,
  recordConsent,
  setKeyQuotes,
  setFactualClaims,
  generateEditorialAngle,
  generateAssignmentEditorialAngle,
  generateAvatarBriefing,
  generateInterviewScript,
  regenerateAllAIContent,
  getAIContentStatus,
  generateBriefingPrompt,
  createPersona,
  getReplicas,
} as const;
